import { format } from 'date-fns';
import { MessageMedium } from 'constants/messageMediums';
import { FrequencyMode, SupportedMediums } from 'modules/frequencyManagement/lib/types';
import { MESSAGE_TYPES } from 'modules/frequencyManagement/contexts/FormProvider';
import { parseDate } from 'utils/datetime';
import { supportedMediums } from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';

export const stringIsMessageMedium = (targetMedium: string, compareMedium: MessageMedium) =>
  targetMedium && targetMedium.toLowerCase() === compareMedium.toLowerCase();

export const isMessageMediumSupported = (messageMedium: MessageMedium) =>
  supportedMediums.includes(messageMedium as SupportedMediums);

export const isFrequencyManaged = (mode: FrequencyMode) =>
  mode === FrequencyMode.Cap || mode === FrequencyMode.Optimize;

export const createDateFormatter = (formatString: string) => (date: string) => {
  if (!date) return '';
  return format(parseDate(date), formatString);
};

export const formatOptimizeLastUpdatedDate = createDateFormatter('M/d/yyyy');

export const formatCSVFilenameDate = createDateFormatter('yyyy-MM-d');

export const mapParameterToMedium = (parameterMedium: string) => {
  return (
    supportedMediums.find((medium) => stringIsMessageMedium(parameterMedium, medium)) ||
    MessageMedium.Email
  );
};

export const createRootName = (index?: number) => {
  /**
   * rootName is prepended to input names for Message Types in a format
   * that is dictated by React Hook Form. Because MessageTypes are stored
   * as a zod array, index is necessary to identify the correct input.
   */
  if (index === undefined) return undefined;
  return `${MESSAGE_TYPES}.${index}`;
};

export const pluckIndexFromRootName = (rootName?: string) => {
  if (!rootName) return undefined;
  const index = rootName.split('.')[1];
  return Number(index);
};

/**
 * The following inputId functions are used to specify unique ids
 * for inputs that can be targeted by the focusing actions in FocusProvider.
 */
export const getCapInputId = (rootName?: string) => {
  if (!rootName) return 'capInput-messageMedium';
  const index = pluckIndexFromRootName(rootName);
  return `capInput-${index}`;
};

export const getOptimizeInputId = (rootName?: string) => {
  if (!rootName) return 'optimizeInput-messageMedium';
  const index = pluckIndexFromRootName(rootName);
  return `optimizeInput-${index}`;
};

export const createGetInputId = (mode: FrequencyMode) =>
  mode === FrequencyMode.Optimize ? getOptimizeInputId : getCapInputId;
